import { useCallback } from 'react';

import { useClient } from 'urql';

import { useAppState, useFunctionPolling } from '@sg/garnish';
import { OrderState } from '@sg/graphql-schema';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsBagOpen } from '../useBagNavigation/useBagNavigation';
import { CartDocument, type CartQuery } from '../useCart/GraphQL/Cart.generated';
import { useCart } from '../useCart/useCart';
import { useResetGroupOrderingState } from './useResetGroupOrderingState';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Synchronizes active group-order state by polling while the app is active and
 * outside Bag, then clears stale frontend state when the order ends or disappears.
 */
export const useGroupOrderingState = () => {
  const client = useClient();
  const { cart } = useCart();
  const isBagOpen = useIsBagOpen();

  const resetGroupOrderingState = useResetGroupOrderingState();

  const pollingIntervalSeconds = useFeatureFlag('CELS-5826-order-group-order-polling-interval-seconds'); // prettier-ignore

  // ─── Derived ───────────────────────────────────────────────────────────────

  const pollingInterval = pollingIntervalSeconds * MILLISECONDS_PER_SECOND;

  const isGroupOrder = Boolean(cart?.multipartyDetails);
  const isGroupOrderFinished = checkIfGroupOrderFinished(cart);
  const isPollingEnabled = isGroupOrder && !isGroupOrderFinished && !isBagOpen && pollingIntervalSeconds > 0; // prettier-ignore

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handlePoll = useCallback(async () => {
    if (!isPollingEnabled) return;

    const response = await client
      .query<CartQuery>(CartDocument, {}, { requestPolicy: 'network-only' })
      .toPromise();

    const didPollingFail = Boolean(response.error) || !response.data;

    if (didPollingFail) return;

    const hasGroupOrderEnded = checkIfGroupOrderFinished(response.data?.cart);

    if (!hasGroupOrderEnded) return;

    resetGroupOrderingState();
  }, [client, isPollingEnabled, resetGroupOrderingState]);

  const handleAppVisible = useCallback(() => {
    void handlePoll();
  }, [handlePoll]);

  // ─── App State ─────────────────────────────────────────────────────────────

  const appState = useAppState({
    pause: !isPollingEnabled,
    onVisible: handleAppVisible,
  });

  const shouldSkipPolling = !isPollingEnabled || appState !== 'active';

  // ─── Effects ───────────────────────────────────────────────────────────────

  useFunctionPolling(pollingInterval, handlePoll, shouldSkipPolling);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfGroupOrderFinished(cart: CartQuery['cart'] | undefined) {
  return (
    !cart || cart.orderState === OrderState.Complete || cart.orderState === OrderState.Canceled
  );
}

// ─── Constants ───────────────────────────────────────────────────────────────

const MILLISECONDS_PER_SECOND = 1000;
